import React from "react";
import { FormControl, FormGroup, Typography, Stack } from "@mui/material";
import { Checkbox } from "./Checkbox";
import { normalizeOption } from "./normalizeOption";
import { IconWithTooltip } from "common/tooltip";

export interface ICheckboxOption {
  label: string;
  value?: string;
  helperText?: string;
  tooltipTitle?: React.ReactNode;
  disabled?: boolean;
  "data-custom-css-value"?: string; // for form custom css
}

export interface ICheckboxGroupProps {
  label: string;
  options: (string | ICheckboxOption)[];
  values: string[];
  direction?: "row" | "column";
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  helperText?: string;
  tooltipTitle?: React.ReactNode;
}

export const CheckboxGroup: React.FC<ICheckboxGroupProps> = ({
  label,
  options,
  onChange,
  values,
  direction = "column",
  helperText,
  tooltipTitle,
}) => {
  return (
    <FormControl>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="subtitle2" color="text.primary">
          {label}
        </Typography>
        {tooltipTitle && (
          <IconWithTooltip
            tooltipText={tooltipTitle}
            variant="help"
            iconSx={{
              verticalAlign: "middle",
              ml: 0.5,
              pb: 0.3,
            }}
          />
        )}
      </Stack>
      <FormGroup
        sx={{
          flexDirection: direction,
          gap: direction === "row" ? 2 : 0,
        }}
      >
        {options.map((option, index) => {
          const normalizedOption = normalizeOption(option);
          return (
            <Checkbox
              key={index}
              label={normalizedOption.label}
              value={normalizedOption.value!}
              checked={values.includes(normalizedOption.value!)}
              onChange={onChange}
              helperText={normalizedOption.helperText}
              tooltipTitle={normalizedOption.tooltipTitle}
              disabled={normalizedOption.disabled}
              data-custom-css-value={normalizedOption["data-custom-css-value"]}
            />
          );
        })}
      </FormGroup>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};
